/**
 * Conversation List Item Component
 * Reusable component for displaying individual conversations in lists
 */

import React, { useState } from 'react';
import {
  ListItem,
  ListItemButton,
  IconButton,
  Tooltip,
  Box,
  Fade,
  Typography,
  Checkbox,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ChatBubbleOutlineOutlined as ChatIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { ConversationSummary } from '../../api/data-contracts';
import { formatDate } from '../../utils/formatting/formatters';

interface ConversationListItemProps {
  conversation: ConversationSummary;
  isSelected?: boolean;
  showActions?: boolean;
  onSelect: (conversationId: string) => void;
  onDelete: (conversationId: string, title: string) => void;
  onEdit: (conversation: ConversationSummary) => void;
  // Selection mode props
  selectionMode?: boolean;
  isSelectionChecked?: boolean;
  onToggleSelection?: (id: string) => void;
}

export const ConversationListItem: React.FC<ConversationListItemProps> = ({
  conversation,
  isSelected = false,
  showActions = true,
  onSelect,
  onDelete,
  onEdit,
  selectionMode = false,
  isSelectionChecked = false,
  onToggleSelection,
}) => {
  const { t, i18n } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  const handleSelect = () => {
    // In selection mode, toggle selection instead of navigating
    if (selectionMode && onToggleSelection) {
      onToggleSelection(conversation.id);
    } else {
      onSelect(conversation.id);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(conversation.id, conversation.title);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(conversation);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (onToggleSelection) {
      onToggleSelection(conversation.id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect();
    } else if (e.key === 'Delete' && showActions && !selectionMode) {
      e.preventDefault();
      handleDelete(e as any);
    }
  };

  return (
    <ListItem
      disablePadding
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        mb: 1,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateX(4px)',
        },
      }}
      secondaryAction={
        // Only show delete icon in non-selection mode (checkbox is now on left)
        !selectionMode && showActions ? (
          <Fade in={isHovered || isSelected} timeout={200}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                pr: 1,
              }}
            >
              <Tooltip title={t('conversation.delete')} arrow>
                <IconButton
                  size="medium"
                  onClick={handleDelete}
                  color="error"
                  aria-label={t('conversation.delete')}
                  sx={{
                    width: 44,
                    height: 44,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      bgcolor: 'error.lighter',
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Fade>
        ) : undefined
      }
    >
      <ListItemButton
        onClick={handleSelect}
        selected={isSelected}
        onKeyDown={handleKeyDown}
        aria-label={`${t('conversation.select')} ${conversation.title}`}
        sx={{
          py: 1.5,
          px: 2,
          borderRadius: 2,
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : 'transparent',
          bgcolor: isSelected
            ? 'primary.lighter'
            : isSelectionChecked
              ? 'action.selected'
              : isHovered
                ? 'action.hover'
                : 'transparent',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            bgcolor: isSelected ? 'primary.lighter' : 'action.hover',
            borderColor: isSelected ? 'primary.main' : 'action.selected',
          },
          '&.Mui-focusVisible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
          },
          // Make room for action buttons (only in non-selection mode)
          pr: !selectionMode && showActions ? 8 : 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%', minWidth: 0 }}>
          {/* Selection checkbox on left */}
          {selectionMode && (
            <Checkbox
              checked={isSelectionChecked}
              onChange={handleCheckboxChange}
              onClick={(e) => e.stopPropagation()}
              color="primary"
              size="small"
              aria-label={t('conversation.select')}
              sx={{
                mt: -0.5,
                ml: -1,
                transition: 'all 0.2s ease-in-out',
              }}
            />
          )}

          {/* Chat icon indicator */}
          <Box
            sx={{
              mt: 0.5,
              color: isSelected ? 'primary.main' : 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              transition: 'color 0.2s ease-in-out',
            }}
          >
            <ChatIcon fontSize="small" />
          </Box>

          {/* Text content */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Tooltip title={conversation.title} arrow placement="top" enterDelay={500}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isSelected ? 600 : 500,
                  color: isSelected ? 'primary.main' : 'text.primary',
                  mb: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  transition: 'color 0.2s ease-in-out',
                }}
              >
                {conversation.title}
              </Typography>
            </Tooltip>

            {conversation.last_message_preview && (
              <Tooltip title={conversation.last_message_preview} arrow placement="bottom" enterDelay={500}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mb: 0.5,
                  }}
                >
                  {conversation.last_message_preview}
                </Typography>
              </Tooltip>
            )}

            {/* Metadata row */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.disabled',
                  fontSize: '0.7rem',
                }}
              >
                {t('conversation.messages', { count: conversation.message_count || 0 })}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.disabled',
                  fontSize: '0.7rem',
                }}
              >
                •
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.disabled',
                  fontSize: '0.7rem',
                }}
              >
                {formatDate(conversation.updated_at, i18n.language)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </ListItemButton>
    </ListItem>
  );
};